import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  type ExtractedTableJson,
  invalidateAllQuoteItemMatchesQueries,
  invalidateExtractedTablesQuery,
  useDismissExtractedTable,
} from '@frontend/api';

import { useWebViewerContext } from 'modules/common/viewer/pdfViewer/WebViewerContext';
import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  getKeyForExtractedTableJson,
} from 'modules/common/viewer/pdfViewer/annotationCommands';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';
import { getPendingTableAnnotationStyle } from 'modules/common/viewer/pdfViewer/hooks/useManualTableSelection';

/**
 * Dismisses a BEAMS-extracted table and, once the dismissal has actually succeeded,
 * resubmits the same page region for reprocessing - mirroring the manual "draw a box"
 * flow so the retry gets the exact same dashed placeholder, toast, and cleanup lifecycle
 * for free via `useCroppedTableInteractions` (already mounted, unmodified).
 *
 * The dismiss-then-resubmit ordering is a hard requirement: resubmitting after a failed
 * dismissal would leave two copies of the same BOM table.
 */
export const useRetryExtractedTable = () => {
  const { mutateAsync: dismissExtractedTable } = useDismissExtractedTable();
  const { instance, getAnnotationById } = useWebViewerContext();
  const dispatch = useDispatch();

  const retryExtractedTable = useCallback(
    async (rawTableData: ExtractedTableJson) => {
      if (
        rawTableData.x1 == null ||
        rawTableData.y1 == null ||
        rawTableData.width == null ||
        rawTableData.height == null
      ) {
        throw new Error('Cannot retry a table without page coordinates');
      }

      await dismissExtractedTable({ extractedTableId: rawTableData.id });

      invalidateExtractedTablesQuery({ partFileUuid: rawTableData.partFileId });
      invalidateAllQuoteItemMatchesQueries();

      if (!instance) {
        return;
      }

      const { annotationManager } = instance.Core;
      const existingAnnotation = getAnnotationById(
        getKeyForExtractedTableJson(rawTableData),
      );
      if (existingAnnotation) {
        annotationManager.hideAnnotation(existingAnnotation);
      }

      const placeholderAnnotation =
        new instance.Core.Annotations.RectangleAnnotation({
          PageNumber: rawTableData.pageNumber,
        });
      placeholderAnnotation.setRect(
        instance.Core.Math.Rect.createFromDimensions(
          rawTableData.x1,
          rawTableData.y1,
          rawTableData.width,
          rawTableData.height,
        ),
      );
      annotationManager.setAnnotationStyles(
        placeholderAnnotation,
        getPendingTableAnnotationStyle(instance),
      );
      annotationManager.addAnnotation(placeholderAnnotation);
      annotationManager.redrawAnnotation(placeholderAnnotation);

      dispatch(
        suggestionAnnotations.queueCommands({
          commands: [
            {
              type: SUGGESTION_ANNOTATION_COMMAND_TYPES.REFRESH_EXTRACTED_TABLES,
              payload: {
                pageNumber: rawTableData.pageNumber,
                x1: rawTableData.x1,
                y1: rawTableData.y1,
                x2: rawTableData.x1 + rawTableData.width,
                y2: rawTableData.y1 + rawTableData.height,
                cleanUpTempAnnotation: () => {
                  annotationManager.deleteAnnotation(placeholderAnnotation);
                },
              },
            },
          ],
        }),
      );
    },
    [dismissExtractedTable, instance, getAnnotationById, dispatch],
  );

  return { retryExtractedTable };
};
